import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AnalysisExportStatus } from '@api-types/enums/analysis-export-status.enum';
import {
  AnalysisExportSseEventPayload,
  CompletedExportSseEventPayload,
} from '@api-types/sse/event-payloads/analysis-export.sse-event-payload';
import { SseEventType } from '@api-types/sse/sse.types';
import { SseEventHandler } from '@core/sse/sse-event-handler';
import { selectQueryParam } from '@core/store/router-state/router.selectors';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  AnalysisExportDialogComponent,
  AnalysisExportDialogData,
  ExportTab,
} from 'demand-forecasting/analysis/components/dialogs/analysis-export-dialog/analysis-export-dialog.component';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import { DEMAND_PAGE_TAB_QUERY_KEY } from 'demand-forecasting/pages/forecast-detail/forecast-detail-page.component';
import { ConfigurationRouterHelperService } from 'demand-forecasting/store/router-state/configuration-router-helper.service';
import { selectConfigurationId } from 'demand-forecasting/store/router-state/router.selectors';
import { ForecastTab } from 'demand-forecasting/types/forecast-tab.enum';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs';

@Injectable()
export class AnalysisExportSseHandler implements SseEventHandler<
  typeof SseEventType.AnalysisExport
> {
  private readonly store = inject(Store);
  private readonly toast = inject(ToastrService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly configurationRouterHelper = inject(
    ConfigurationRouterHelperService,
  );
  private readonly analysisStateService = inject(AnalysisStateService);

  private readonly currentConfigurationId = this.store.selectSignal(
    selectConfigurationId,
  );
  private readonly currentDemandTab = this.store.selectSignal(
    selectQueryParam(DEMAND_PAGE_TAB_QUERY_KEY),
  );

  readonly eventType = SseEventType.AnalysisExport;

  handle(payload: AnalysisExportSseEventPayload): void {
    this.analysisStateService.exports.reload();
    switch (payload.status) {
      case AnalysisExportStatus.COMPLETED:
        this.showExportReadyToast(payload);
        break;
      case AnalysisExportStatus.FAILED:
        this.showExportFailedToast();
        break;
      default:
        // no action for other statuses
        break;
    }
  }

  private showExportReadyToast(payload: CompletedExportSseEventPayload): void {
    const toast = this.toast.success(
      this.translate.instant('ANALYSIS.EXPORT.TOAST_CLICK_TO_VIEW'),
      this.translate.instant('ANALYSIS.EXPORT.TOAST_READY_TITLE'),
      {
        timeOut: 0, // stays until manually dismissed
        extendedTimeOut: 0,
        tapToDismiss: false,
        closeButton: true,
      },
    );

    toast.onTap.pipe(take(1)).subscribe(() => {
      this.toast.clear(toast.toastId);
      this.navigateToExport(payload);
    });
  }

  private showExportFailedToast(): void {
    this.toast.error(
      this.translate.instant('ANALYSIS.EXPORT.TOAST_FAILED_MESSAGE'),
      this.translate.instant('ANALYSIS.EXPORT.TOAST_FAILED_TITLE'),
    );
  }

  private navigateToExport(payload: CompletedExportSseEventPayload): void {
    const nav = payload.navigationHelper;

    const isOnSameConfiguration =
      this.currentConfigurationId() === nav.configurationId;
    const isOnAnalysisTab = this.currentDemandTab() === ForecastTab.ANALYSIS;

    // only skip navigation if already on correct configuration AND analysis tab
    if (isOnSameConfiguration && isOnAnalysisTab) {
      this.openExportDialog(payload.exportId);
      return;
    }

    const baseQueryParams =
      this.configurationRouterHelper.getInitialQueryParams({
        latestVersionId: nav.latestForecastVersionId,
        defaultResolution: nav.resolution,
        defaultAdditionalTimeSeries: nav.additionalTimeSeries,
        defaultFilterCombinations: nav.filterCombinations,
        defaultTargetUnit: nav.targetUnit,
      });

    const urlTree = this.router.createUrlTree(
      ['/demand/configurations', nav.configurationId],
      {
        queryParams: {
          ...baseQueryParams,
          demandTab: ForecastTab.ANALYSIS,
          analysisVersionId: nav.analysisVersionId,
          analysisConfigurationId: nav.analysisConfigurationId,
          analysisHierarchy: nav.hierarchy,
        },
      },
    );

    this.router.navigateByUrl(urlTree).then((success) => {
      if (success) {
        this.openExportDialog(payload.exportId);
      } else {
        console.error('navigation to export failed');
      }
    });
  }

  private openExportDialog(highlightExportId: string): void {
    const dialogData: AnalysisExportDialogData = {
      initialTab: ExportTab.LIST,
      highlightExportId,
    };

    this.dialog.open(AnalysisExportDialogComponent, {
      data: dialogData,
      autoFocus: false, // prevent focus on first element (wrong tab)
    });
  }
}
